import { isEqual } from "lodash";

import type { FormFieldValueType } from "../../../mobile/core/data-forms/form-field";
import type { FormCollectionInput, FormImageInput, FormInput } from "../../core/data-forms/form-input-types";
import { FormInputType } from "../../core/data-forms/form-input-types";
import { logger } from "../../core/logging/logger";
import { isDefined } from "../../utils/assert";
import { formatCollectionInputs } from "../../utils/format-collection-inputs";
import type { ImagesConverter, UploadedImage } from "../../utils/images-converter";
import { Observable } from "../../utils/observable";
import type { UrlLink } from "../BaseUrl";
import type { FeaturesManager } from "../features/features-manager";
import type { InitialOnboarding, OnboardingFile } from "./onboarding";
import { OnboardingStatus } from "./onboarding";
import type { OnboardingService } from "./onboarding-service";

export class OnboardingManager {
  constructor(
    private readonly onboardingService: OnboardingService,
    private readonly featuresManager: FeaturesManager,
    private readonly imagesConverter: ImagesConverter,
  ) {}

  onboardingFile = new Observable<OnboardingFile | null>(null);
  steps = new Observable<FormCollectionInput[]>([]);
  updated = new Observable<boolean>(true);
  /**
   * True while a picked image is being turned into a PDF. That conversion is asynchronous and
   * nobody awaits it, so without this the user can reach the next step before the document is
   * ready and submit an empty — or stale — field.
   */
  converting = new Observable<boolean>(false);
  private pendingConversions = 0;
  updating = new Observable<boolean>(false);
  submitting = new Observable<boolean>(false);
  error = new Observable<string | null>(null);
  needOnboardingFileRedirect = new Observable<boolean>(false);
  allRequiredFieldsFilled = new Observable<boolean>(false);

  /**
   * The call in flight, shared by every caller. A screen can mount several components that each
   * ask for the file — the menu, the screen, its alert cards — and each answer used to publish a
   * fresh object, redrawing every observer as many times.
   */
  private initializing: ReturnType<OnboardingManager["fetchOnboardingFile"]> | null = null;

  async initialize() {
    if (!this.initializing) {
      this.initializing = this.fetchOnboardingFile().finally(() => {
        this.initializing = null;
      });
    }
    return this.initializing;
  }

  private async fetchOnboardingFile() {
    if (this.featuresManager.features.get().sdaCustomerOnboardingView) {
      try {
        const onboardingFile = await this.onboardingService.getOnboardingFile();
        if (onboardingFile) {
          this.onboardingFile.set(onboardingFile);
          const needOnboardingFileRedirect = [OnboardingStatus.INITIATED].includes(
            onboardingFile?.status as OnboardingStatus,
          );
          this.needOnboardingFileRedirect.set(needOnboardingFileRedirect);
          this.setFilteredSteps(onboardingFile.form.inputs);
          return {
            onboardingFile,
            needOnboardingFileRedirect,
          };
        }
      } catch (e) {
        if (e === "CLIENT_DELEGATE_USER_NOT_ALLOWED") {
          throw e;
        } else {
          logger.debug("error getting onboarding file", e);
        }
      }
    }
  }

  async initiate(data: InitialOnboarding) {
    try {
      await this.onboardingService.initiateOnboarding(data);
    } catch (e) {
      logger.debug("error initiating onboarding", e);
      throw e?.response?.data?.error?.message ?? e.toString();
    }
  }

  async updateOnboardingFile(stepId: string) {
    this.clearError();
    this.updating.set(true);
    try {
      const rawCollection = this.getStepById(stepId);
      const stepValues = this.getFlatCollectionData(rawCollection);
      const hasMissingRequiredFields = this.valuesHaveMissingRequiredFields(rawCollection.inputs);
      const hasValidationErrors = this.valuesHaveValidationErrors(rawCollection.inputs);

      if (!hasMissingRequiredFields.allCorrect) {
        throw JSON.stringify({
          message: "Missing required fields",
          fields: hasMissingRequiredFields.errorFields,
        });
      }

      if (!hasValidationErrors.allCorrect) {
        throw JSON.stringify({
          message: "Invalid fields",
          fields: hasValidationErrors.errorFields,
        });
      }

      if (this.updated.get()) {
        const onboardingFile = await this.onboardingService.updateOnboardingFile(stepValues);
        this.onboardingFile.set(onboardingFile);
        this.needOnboardingFileRedirect.set(
          [OnboardingStatus.INITIATED].includes(onboardingFile?.status as OnboardingStatus),
        );
        this.setFilteredSteps(onboardingFile.form.inputs);
        this.updated.set(false);
      }
    } catch (e) {
      let JSONError;
      try {
        JSONError = JSON.parse(e);
      } catch {
        JSONError = null;
      }

      if (JSONError?.message === "Invalid fields") {
        this.error.set("Certains champs sont incorrects.");
        throw JSONError;
      } else if (JSONError?.message === "Missing required fields") {
        this.error.set("Certains champs sont requis.");
        throw JSONError;
      } else {
        this.error.set(e?.response?.data?.error?.message ?? e.toString());
        throw e?.response?.data?.error?.message ?? e.toString();
      }
    } finally {
      this.updating.set(false);
    }
  }

  hasInputValueChanged(value: FormFieldValueType, id: string, stepId: string) {
    const onboardingFile = this.onboardingFile.get();
    if (onboardingFile) {
      const step = onboardingFile?.form.inputs.find((s) => s.id === stepId);
      if (!step) return false;
      const oldValue = getInput(step, id).value;
      return !isEqual(oldValue, value);
    }
    return false;
  }

  stringToRegex(str: string) {
    const main = str.match(/\/(.+)\/.*/);
    const options = str.match(/\/.+\/(.*)/);
    if (main && main.length > 1 && options && options.length > 1) {
      return new RegExp(main[1], options[1]);
    }
    return null;
  }

  valuesHaveMissingRequiredFields(inputs: FormInput[]) {
    const result = {
      allCorrect: true,
      errorFields: [] as string[],
    };
    const requiredFields = inputs.filter((e) => e.required);
    requiredFields.forEach((input) => {
      const requiredFieldValue = input.value;
      if (
        (!requiredFieldValue && requiredFieldValue !== 0) ||
        requiredFieldValue === "" ||
        requiredFieldValue === null ||
        requiredFieldValue === undefined
      ) {
        //value not present or empty
        result.allCorrect = false;
        result.errorFields.push(input.id);
      }
    });
    return result;
  }

  valuesHaveValidationErrors(inputs: FormInput[]) {
    const result = {
      allCorrect: true,
      errorFields: [] as string[],
    };
    inputs.forEach((input) => {
      const fieldValue = input.value;
      if (!!input.validators && !!fieldValue) {
        if (
          input.validators.some((rule) => {
            //if one of the validators is evaluated to false
            const regex = this.stringToRegex(rule);
            if (regex) {
              return !regex.test(fieldValue?.toString());
            }
            return false;
          })
        ) {
          result.allCorrect = false;
          result.errorFields.push(input.id);
        }
      }
    });
    return result;
  }

  getFlatCollectionData(collection: FormCollectionInput) {
    const data: { [key: string]: FormFieldValueType } = {};
    collection.inputs.forEach((input: FormInput) => {
      const fieldIsFileAndAlreadyUploaded = input.type === FormInputType.FileContent && typeof input.value !== "string";
      if (!fieldIsFileAndAlreadyUploaded) {
        data[input.id] = input.value as string;
      }
    });
    return {
      [collection.id]: data,
    };
  }

  fillResponse(stepId: string, input: FormInput, value: FormFieldValueType) {
    const steps = this.steps.get();
    const currentStep = steps.find((s) => s.id === stepId);
    if (!currentStep) throw new Error(`Step ${stepId} not found`);

    if (this.hasInputValueChanged(value, input.id, stepId)) {
      this.updated.set(true);
      getInput(currentStep, input.id).value = value;
      // Prevent app freeze when updating a lot of inputs
      setTimeout(() => {
        this.steps.set([...steps]);
      }, 0);
    }
  }

  async fillMultiImagesResponse(stepId: string, input: FormInput, values: (string | UploadedImage)[]) {
    const isReady =
      values.reduce<number>((prev, value) => prev + (value ? 1 : 0), 0) >=
      ((input as FormImageInput).minimumPageCount ?? 1);

    let pdfDocument: string | undefined;
    if (isReady) {
      this.startConversion();
      try {
        const filteredValues = values.filter((value) => value);
        try {
          const converted = await this.imagesConverter.toPdf(filteredValues);
          pdfDocument = isDefined(converted) ? await this.imagesConverter.toBase64(converted) : undefined;
        } catch (e) {
          // The field keeps what it held: writing the failure into it would read as a page the
          // holder never removed, and the form would only say the field is missing.
          logger.debug("error converting the pages to a document", e);
          this.error.set("Le document n'a pas pu être préparé. Réessayez ou choisissez un autre fichier.");
          return;
        }
        if (!isDefined(pdfDocument)) {
          logger.debug("the pages produced no document");
          this.error.set("Le document n'a pas pu être préparé. Réessayez ou choisissez un autre fichier.");
          return;
        }
      } finally {
        this.endConversion();
      }
    }
    const steps = this.steps.get();
    const currentStep = steps.find((s) => s.id === stepId);
    if (!currentStep) throw new Error(`Step ${stepId} not found`);

    this.updated.set(true);
    const filledInput = getInput(currentStep, input.id) as FormImageInput;
    // Without the pages the field keeps no trace of what was already taken, so ImageField
    // resets its own list every time one is added — silently dropping the previous ones. The
    // register manager has always recorded them; only this one had been forgotten.
    filledInput.values = values as FormImageInput["values"];
    filledInput.value = pdfDocument;
    setTimeout(() => {
      this.steps.set([...steps]);
    }, 0);
  }

  // Counted rather than a plain flag: adding two pages in a row overlaps two conversions.
  private startConversion() {
    this.pendingConversions += 1;
    this.converting.set(true);
  }

  private endConversion() {
    this.pendingConversions = Math.max(0, this.pendingConversions - 1);
    this.converting.set(this.pendingConversions > 0);
  }

  async submitOnboardingFile() {
    this.clearError();
    this.submitting.set(true);
    try {
      await this.onboardingService.submitOnboardingFile();
      this.updated.set(false);
      return await this.initialize();
    } catch (e) {
      logger.debug("error submitting onboarding file", e);
      this.error.set(e?.response?.data?.error?.message ?? e.toString());
    } finally {
      this.submitting.set(false);
    }
  }

  async getAttachmentPreview(attachmentId: string, links?: UrlLink[]) {
    try {
      const url = links?.find((l) => l.rel === "getDocumentPreview")?.href;
      const preview = await this.onboardingService.getAttachmentPreview(attachmentId, url);
      return preview;
    } catch (e) {
      logger.debug("error getting attachment preview", e);
    }
  }

  public clearError() {
    this.error.set(null);
  }

  private setFilteredSteps(inputs: FormCollectionInput[]) {
    const filteredSteps = formatCollectionInputs(inputs);
    this.steps.set(filteredSteps);
  }

  private getStepById(stepId: string) {
    const steps = this.steps.get();
    const step = steps.find((s) => s.id === stepId);
    if (!step) throw new Error(`Step ${stepId} not found`);
    return step;
  }
}

function getInput(collection: FormCollectionInput, inputId: string) {
  const input = collection.inputs.find((i: { id: string }) => i.id === inputId);

  if (!input) {
    throw new Error(`can't find input ${inputId} in collection ${collection.id}`);
  }

  return input;
}

export class OnboardingMissingFieldsError extends Error {
  constructor(msg, fields) {
    super(msg);
    this.fields = fields;
  }
  public fields: string[];
}
